import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { uploadFile } from "@/network/network";
import { formatFile } from "@/modern/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import type { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import type { Location } from "@/bryntum-lib/models/Location";
import { useSwivuStore } from "../useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";

export const filterLocationPhotoFiles = (files: FileList | File[]) =>
  Array.from(files).filter((file) => file.type.startsWith("image/"));

export const useLocationPhotos = (locationRecord: Location | null | undefined) => {
  const { project, user } = useSwivuBryntumContext();
  const [photos, setPhotos] = useState<FormattedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { subscribe } = useSwivuStore<BoardFile>({ storeId: STORE_KEYS.boardFilesStore });

  const refreshPhotos = useCallback(() => {
    if (!locationRecord?.id) {
      setPhotos([]);
      return;
    }
    const files = project.boardFilesStore.query(
      (item: { locationId: number | string }) => item.locationId === locationRecord.id
    );
    setPhotos(formatFile(files, user));
  }, [project, locationRecord?.id, user]);

  const uploadPhotos = useCallback(
    async (uploadedFiles: FileList | File[]) => {
      if (!locationRecord?.id) return;
      const uploadedFileArray = Array.from(uploadedFiles);
      const fileArray = filterLocationPhotoFiles(uploadedFileArray);
      const rejectedCount = uploadedFileArray.length - fileArray.length;

      if (rejectedCount > 0) {
        toast.error("Only image files can be uploaded as location photos.");
      }
      if (fileArray.length === 0) return;

      const fileCount = fileArray.length;
      const fileText = fileCount === 1 ? "photo" : "photos";

      setIsUploading(true);
      try {
        toast.loading(`Uploading ${fileCount} ${fileText}...`, { id: "location-photo-upload" });

        const uploadPromises = fileArray.map(async (file) => {
          const { data: uploadData } = await uploadFile(file, file.name);
          return uploadData.id;
        });

        const documentIds = await Promise.all(uploadPromises);
        const records = documentIds.map((documentId) => ({
          documentId,
          location: locationRecord.id,
        }));

        project.boardFilesStore.add(records);
        await project.sync();

        toast.success(`Successfully uploaded ${fileCount} ${fileText}`, { id: "location-photo-upload" });
        refreshPhotos();
      } catch (error) {
        console.error("Location photo upload error:", error);
        toast.error(`Failed to upload ${fileText}. Please try again.`, { id: "location-photo-upload" });
      } finally {
        setIsUploading(false);
      }
    },
    [locationRecord?.id, project, refreshPhotos]
  );

  const removePhoto = useCallback(
    async (id: string | number) => {
      try {
        project.boardFilesStore.remove(id);
        await project.sync();
        toast.success("Photo removed");
        refreshPhotos();
      } catch (error) {
        console.error("Location photo remove error:", error);
        toast.error("Failed to remove photo. Please try again.");
      }
    },
    [project, refreshPhotos]
  );

  useEffect(() => {
    refreshPhotos();

    return subscribe(({ record }) => {
      if (record.locationId === locationRecord?.id) {
        refreshPhotos();
      }
      return record.locationId === locationRecord?.id;
    })
  }, [refreshPhotos, subscribe, locationRecord]);

  return { photos, isUploading, uploadPhotos, removePhoto, refreshPhotos };
};
